import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { getAccessToken, getRefreshToken } from "./tokens";
import { jwtDecode } from "jwt-decode";
import { fullAPIURL } from "./requests";
import Cookies from "js-cookie";

// Define global axios interceptor
export const refreshAccessTokenInterceptor = async <T>(
  config: AxiosRequestConfig<T>,
) => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return config;
  }
  await refreshAccessToken(accessToken);
  return config;
};

/**
 * isFetching is a simple toggle to prevent multiple requests to the refresh endpoint before the first one is resolved
 */
let isFetching = false;
/**
 * checks if the access token is expired and refreshes it if necessary
 * if the refresh fails, the user is logged out
 *
 * returns the original access token if no refresh token is found
 * @param accessToken - The access token to refresh
 * @returns the new access token and the refresh token
 */
export async function refreshAccessToken(accessToken: string) {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.log("No refresh token found");
    return {
      accessToken,
      refreshToken,
    };
  }

  const decodedToken = jwtDecode<{ exp: number }>(accessToken);

  const buffer = 1000 * 60; // 1 minute buffer

  // if is fetching then set a timeout to check again in 50ms
  if (isFetching) {
    return new Promise<{ accessToken: string; refreshToken: string }>(
      (resolve) =>
        setTimeout(() => {
          resolve(refreshAccessToken(accessToken));
        }, 50),
    );
  }

  const accessTokenExpirationInterval = decodedToken.exp * 1000 - Date.now();
  if (accessTokenExpirationInterval <= buffer) {
    console.log(
      `Refreshing ${
        accessTokenExpirationInterval > 0 ? "about to expire" : "expired"
      } access token`,
    );
    isFetching = true;
    try {
      const {
        data: { refreshToken: newRefreshToken, accessToken: newAccessToken },
      } = await axios.get<{
        refreshToken: string;
        accessToken: string;
      }>(`${fullAPIURL}/v2/auth/token/access/user`, {
        headers: {
          authorization: `Bearer ${refreshToken}`,
        },
      });

      if (newAccessToken.length > 0 && newRefreshToken.length > 0) {
        console.log("Received new access and refresh tokens");
      } else {
        console.error("Did not receive new access and refresh tokens");
      }

      Cookies.set("ACCESS_TOKEN", newAccessToken);
      Cookies.set("REFRESH_TOKEN", newRefreshToken);
      isFetching = false;
      return { accessToken: newAccessToken, refreshToken: newRefreshToken };
    } catch (error) {
      isFetching = false;
      const axiosError = error as AxiosError;
      console.error(
        `Unable to refresh tokens - status: ${axiosError.response?.status}, data: `,
        axiosError.response?.data,
      );
      document.location.href = "/account/logout";
    }
  }
  return { accessToken, refreshToken };
}
